import { unknownToken } from '@shared/data/token'
import { format, formatDistanceToNow } from 'date-fns'
import { TokenType } from '@injectivelabs/sdk-ts/types'
import {
  isIbcTokenCanonical,
  formatNonCanonicalIbcTokenName
} from '@shared/utils/ibc'
import {
  base64ToUtf8,
  stringToUint8Array,
  uint8ArrayToBase64,
  getInjectiveAddress
} from '@injectivelabs/sdk-ts/utils'
import { TIME_AGO_DATE_FORMAT } from '@/app/utils/constant'
import { MetaType } from '@/types'
import type { TokenStatic } from '@injectivelabs/sdk-ts'

export const shortenTimestamp = (timestamp: string): string =>
  timestamp
    .replace('minutes', 'mins')
    .replace('minute', 'min')
    .replace('seconds', 'secs')
    .replace('second', 'sec')

export const formatTickSizeFromDecimals = (decimals: number): string => {
  return `0.${new Array(decimals).join('0')}1`
}

export const formatTickSizeFromTensMultiplier = (
  tensMultiplier: number
): string => {
  const tensMultiplierInBigNumber = toBigNumber(tensMultiplier)

  if (tensMultiplierInBigNumber.gt(0)) {
    return toBigNumber(10).pow(tensMultiplier).toFixed()
  }

  return toBigNumber(1).shiftedBy(tensMultiplier).toFixed()
}

export const formatToPercentage = (value: number | string): string => {
  const valueToNumber = !isNaN(Number(value)) ? Number(value) : 0

  return `${valueToNumber * 100}`
}

export const formatMeta = (metaType: MetaType) => {
  return {
    title: getTitle(metaType),
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: getDescription(metaType)
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: getDescription(metaType)
      },
      {
        hid: 'twitter:description',
        property: 'twitter:description',
        content: getDescription(metaType)
      },
      {
        hid: 'twitter:title',
        property: 'twitter:title',
        content: getTitle(metaType)
      },
      {
        hid: 'og:title',
        property: 'og:title',
        content: getTitle(metaType)
      },
      {
        hid: 'twitter:title',
        property: 'twitter:title',
        content: getTitle(metaType)
      }
    ]
  }
}

export const getTitle = (metaType: MetaType): string => {
  if (
    [
      MetaType.Block,
      MetaType.Blocks,
      MetaType.Transaction,
      MetaType.Transactions
    ].includes(metaType)
  ) {
    return `Injective ${metaType} Explorer - Visualize and search for ${metaType.toLowerCase()} on the Injective Chain`
  }

  if (
    [
      MetaType.Code,
      MetaType.Codes,
      MetaType.Contract,
      MetaType.Contracts
    ].includes(metaType)
  ) {
    return `Injective ${metaType} Explorer - Visualize cosmwasm smart contracts on the Injective Chain`
  }

  return 'Injective Exchanges Explorer - Visualize all exchanges data build on Injective Chain'
}

export const getDescription = (metaType: MetaType): string => {
  if (
    [
      MetaType.Block,
      MetaType.Blocks,
      MetaType.Transaction,
      MetaType.Transactions
    ].includes(metaType)
  ) {
    return `The Injective ${metaType} Explorer enables to search addresses, blocks, transactions, exchange data, trades, tokens, validators, cosmwasm smart contracts over the Injective Chain.`
  }

  if ([MetaType.Contract, MetaType.Contracts].includes(metaType)) {
    return `The Injective ${metaType} Explorer enables to find cosmwasm smart contracts instances over the Injective Chain.`
  }

  if ([MetaType.Code, MetaType.Codes].includes(metaType)) {
    return `The Injective ${metaType} Explorer enables to find cosmwasm smart contracts codes over the Injective Chain.`
  }

  return 'The Injective Exchanges Explorer provides information all the exchanges build on top of Injective Chain.'
}

export const formatTimestamp = (timestamp: string | number) => {
  const date = new Date(timestamp)

  return `${format(date, TIME_AGO_DATE_FORMAT)} (${formatDistanceToNow(date, {
    addSuffix: true
  })})`
}

export const subaccountToInj = (address: string): string => {
  const ethAddress = address.slice(0, 42)

  if (address.length !== 66) {
    return ''
  }

  return getInjectiveAddress(ethAddress)
}

export const formatToBase64 = (value: string) => {
  const [encodedString] = uint8ArrayToBase64(stringToUint8Array(value)).split(
    '='
  )

  return encodedString
}

// convert trade order_hash to order_id
export const formatFromBase64 = (value: string) => {
  try {
    return base64ToUtf8(value)
  } catch {
    // Handle URL-safe base64 by converting to standard format
    const normalizedValue = value
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(value.length / 4) * 4, '=')

    return base64ToUtf8(normalizedValue)
  }
}

export const formatJSONMsg = (value?: string | Record<string, any>) => {
  if (!value || typeof value === 'object') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

export const formatTokenName = (token: TokenStatic) => {
  if (!token) {
    return ''
  }

  if (!isIbcTokenCanonical(token) && token.tokenType === TokenType.Ibc) {
    return formatNonCanonicalIbcTokenName(token)
  }

  if (token.name === unknownToken.name) {
    return token.denom
  }

  return token.name || token.symbol || ''
}

export const wasmErrorToMessageArray = (error: any): string[] => {
  const messageSupportedRegex =
    /Messages supported by this contract: (.*?)(?=: query wasm contract failed: unknown request)/
  const contentMatch = messageSupportedRegex.exec(error.message)

  if (contentMatch && contentMatch[1]) {
    const content = contentMatch[1].split(',')

    return content.map((each) => each.trim())
  }

  const matches = error.message.match(/`(.*?)`/g)

  return matches
    ? matches.slice(1).map((match: string) => match.replace(/`/g, ''))
    : []
}

export const stringifyJson = (value: any) => JSON.stringify(value, null, 2)

export const isJsonString = (value: string): boolean =>
  value.startsWith('{') || value.startsWith('[')

export const recursiveParseJson = (value: any): any => {
  if (typeof value === 'string') {
    try {
      return isJsonString(value) ? JSON.parse(value) : value
    } catch {
      return value
    }
  }

  if (Array.isArray(value)) {
    return value.map(recursiveParseJson)
  }

  if (typeof value === 'object' && value !== null) {
    return Object.entries(value).reduce(
      (objectDictionary, [key, val]) => {
        objectDictionary[key] = recursiveParseJson(val)

        return objectDictionary
      },
      {} as Record<string, any>
    )
  }

  return value
}

export const formatObjectWithJsonToPlainObject = (
  obj: Record<string, any>
): Record<string, any> => {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      acc[key] = recursiveParseJson(value)

      return acc
    },
    {} as Record<string, any>
  )
}

export const assetFormatter = (token: undefined | TokenStatic) => {
  const isUnknownToken = token ? token.tokenType === TokenType.Unknown : false

  const tokenName = token ? formatTokenName(token) : ''

  const tokenNameFormatted =
    isUnknownToken ||
    tokenName.includes('factory') ||
    tokenName.startsWith('ibc/')
      ? `${tokenName.slice(0, 10)}...${tokenName.slice(-10)}`
      : tokenName

  const symbolNameFormatted = token?.symbol.toLowerCase().startsWith('factory/')
    ? `${token.symbol.slice(0, 10)}...${token.symbol.slice(-10)}`
    : token?.symbol || ''

  return {
    tokenName,
    isUnknownToken,
    tokenNameFormatted,
    symbolNameFormatted
  }
}
